import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  LocationDataResponse,
  PropertyDataResponse,
  PropertyAddressParams,
  PropertyCodesResponse
} from 'src/models/location-data.model';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class LocationDataService {
  private readonly BASE_URL = 'https://location-api.tidy.com/api/v1';
  private readonly API_KEY = 'ldsvc_tidy_2024_xK9mP4qR7vN3wL6j';
  private readonly WEATHER_CACHE_DURATION = 12 * 60 * 60 * 1000;
  private readonly PROPERTY_CACHE_DURATION = 7 * 24 * 60 * 60 * 1000;
  private readonly PROPERTY_CODES_CACHE_DURATION = 90 * 24 * 60 * 60 * 1000;
  private locationDataCache = new Map<string, CacheEntry<LocationDataResponse>>();
  private propertyDataCache = new Map<string, CacheEntry<PropertyDataResponse>>();
  private propertyCodesCache = new Map<string, CacheEntry<PropertyCodesResponse>>();
  private httpOptions = {
    headers: new HttpHeaders({
      'X-Api-Key': this.API_KEY
    })
  };

  constructor(private http: HttpClient) {}

  async getLocationData(zip: string): Promise<LocationDataResponse> {
    const cacheKey = `location_${zip}`;
    const cached = this.locationDataCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.WEATHER_CACHE_DURATION) {
      return cached.data;
    }
    const response = await firstValueFrom(
      this.http.get<LocationDataResponse>(`${this.BASE_URL}/location_data?zip=${encodeURIComponent(zip)}&attribute_type=all`, this.httpOptions)
    );
    this.locationDataCache.set(cacheKey, { data: response, timestamp: Date.now() });
    return response;
  }

  async getPropertyData(params: PropertyAddressParams): Promise<PropertyDataResponse> {
    const cacheKey = `property_${params.street_address}_${params.zip}`;
    const cached = this.propertyDataCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.PROPERTY_CACHE_DURATION) {
      return cached.data;
    }
    const urlParams = new URLSearchParams({
      street_address: params.street_address,
      city: params.city,
      state: params.state,
      zip: params.zip
    });
    const response = await firstValueFrom(
      this.http.get<PropertyDataResponse>(`${this.BASE_URL}/property_data?${urlParams}`, this.httpOptions)
    );
    this.propertyDataCache.set(cacheKey, { data: response, timestamp: Date.now() });
    return response;
  }

  async getPropertyCodes(zip: string): Promise<PropertyCodesResponse> {
    const cacheKey = `property_codes_${zip}`;
    const cached = this.propertyCodesCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.PROPERTY_CODES_CACHE_DURATION) {
      return cached.data;
    }
    const response = await firstValueFrom(
      this.http.get<PropertyCodesResponse>(`${this.BASE_URL}/location_data?zip=${encodeURIComponent(zip)}&attribute_type=property_codes`, this.httpOptions)
    );
    this.propertyCodesCache.set(cacheKey, { data: response, timestamp: Date.now() });
    return response;
  }

  clearCache(): void {
    this.locationDataCache.clear();
    this.propertyDataCache.clear();
    this.propertyCodesCache.clear();
  }
}
